// File: src/utils/announce.ts
/**
 * @fileoverview Message announcement system with multi-channel delivery
 *
 * Provides comprehensive message delivery through multiple channels:
 * - Visual toast notifications
 * - Screen reader announcements (WCAG 2.1 AA compliant)
 * - Console logging with scope-based grouping (development)
 *
 * Phase 1: Basic channel control and transitional API (current implementation)
 * Phase 2+: Full queuing and deduplication (future - types defined, implementation pending)
 *
 * @author Engineering Team
 * @copyright Cirrus Invicta 2025
 * @version 1.1.0
 * @see docs/Message-Handling-SDS.md Section 6.2 (Announce System)
 */

import toast from '@/services/toast'

/**
 * Configuration options for message announcements
 *
 * Provides comprehensive control over how messages are presented to users through
 * multiple channels: visual toast notifications, screen reader announcements, and
 * console logging for developers.
 *
 * **Phase 1 (Current)**: All options are accepted. Basic channel control (toast, screenReader,
 * console) is functional. Queue and deduplication options are accepted but not yet active.
 *
 * **Phase 2+**: Full queuing and deduplication will be implemented without breaking changes.
 *
 * @example
 * ```typescript
 * // Basic usage with announce() - current API
 * announce('Save successful', 'success')
 *
 * // Granular channel control
 * announce('Background sync complete', 'info', {
 *   toast: false,        // No visual toast
 *   screenReader: true,  // Announce to screen readers
 *   console: false       // Don't log to console
 * })
 *
 * // Phase 2+ features (accepted now, become active later)
 * announce('Retrying connection...', 'warning', {
 *   dedupe: true,         // Prevent duplicate within 5s (Phase 2+)
 *   dedupeWindow: 5000
 * })
 * ```
 */
export interface AnnounceOptions {
    // ===== EXISTING OPTIONS (backward compatible) =====

    /**
     * Toast notification type - affects visual styling
     *
     * @deprecated Use the type parameter in announce() instead.
     * This property exists for backward compatibility only.
     */
    type?: 'success' | 'error' | 'info' | 'warning' | 'debug'

    /**
     * If true, only announce to screen readers (no visual toast)
     *
     * @deprecated Use `{ toast: false, screenReader: true }` instead.
     * This property exists for backward compatibility only.
     */
    srOnly?: boolean

    /**
     * Duration in milliseconds for toast display
     *
     * Default values by type:
     * - error: 4000ms
     * - warning: 4000ms
     * - success: 3000ms
     * - info: 3000ms
     */
    duration?: number

    // ===== NEW CHANNEL CONTROL OPTIONS (Phase 1) =====

    /**
     * Show visual toast notification
     *
     * Default: `true`
     *
     * @example
     * ```typescript
     * // Silent background update - screen reader only
     * announce('Data synced', 'info', { toast: false, screenReader: true })
     * ```
     */
    toast?: boolean

    /**
     * Announce to screen readers via ARIA live region
     *
     * Default: `true`
     *
     * IMPORTANT: Required for WCAG 2.1 AA compliance. Should only be disabled
     * for purely decorative messages that don't convey important information.
     *
     * @example
     * ```typescript
     * // Visual-only decorative message
     * announce('Animation complete', 'info', { screenReader: false })
     * ```
     */
    screenReader?: boolean

    /**
     * Log to console with scope-based grouping
     *
     * Default: `true` in development, `false` in production
     *
     * Console output includes:
     * - Scope-based grouping (e.g., [DOMAINS], [MAIL])
     * - Message type and severity indicators
     * - Timestamp and context information
     * - Error stack traces when available
     *
     * @example
     * ```typescript
     * // Suppress console output even in dev
     * announce('Debug message', 'debug', { console: false })
     * ```
     */
    console?: boolean

    /**
     * Scope identifier for console grouping
     *
     * Used to group console logs by top-level feature area.
     * Should be a single namespace (no dot notation).
     *
     * Default: Inferred from component context or `'APP'`
     *
     * **Recommended scopes**:
     * - `DOMAINS` - Domain management
     * - `MAIL` - Email protection (geo-blocking, allow/block lists, etc.)
     * - `AUTH` - Authentication and authorization
     * - `ORGANIZATIONS` - Organization management
     * - `SETTINGS` - Application settings
     * - `API` - API service layer
     * - `REPORTS` - Reporting and analytics
     *
     * @example
     * ```typescript
     * // Domain management
     * announce('Domain created', 'success', { scope: 'DOMAINS' })
     * // Console: [DOMAINS] Domain created
     *
     * // Email protection features
     * announce('Country blocked', 'info', { scope: 'MAIL' })
     * // Console: [MAIL] Country blocked
     * ```
     */
    scope?: string

    /**
     * Filterable metadata tags for console logging
     *
     * Key-value pairs for categorizing and filtering console output.
     * Displayed in console groups for errors/warnings.
     *
     * @example
     * ```typescript
     * announce('API Error', 'error', {
     *   scope: 'API',
     *   tags: {
     *     endpoint: '/api/domains',
     *     method: 'POST',
     *     statusCode: '400'
     *   }
     * })
     * ```
     */
    tags?: Record<string, string>

    /**
     * Additional context data for console logging
     *
     * Can contain complex objects, arrays, or any data type.
     * Displayed in console groups for errors/warnings.
     *
     * @example
     * ```typescript
     * announce('Validation failed', 'error', {
     *   scope: 'DOMAINS',
     *   extra: {
     *     invalidFields: ['name', 'email'],
     *     attemptedValue: formData
     *   }
     * })
     * ```
     */
    extra?: Record<string, unknown>

    /**
     * ARIA politeness level for screen reader announcements
     *
     * Default: `'polite'`
     *
     * - `polite`: Wait for user to finish current task before announcing (default)
     * - `assertive`: Interrupt user immediately (use sparingly, for critical errors only)
     *
     * @example
     * ```typescript
     * // Critical error requiring immediate attention
     * announce('Session expired, please login again', 'error', {
     *   politeness: 'assertive'
     * })
     * ```
     */
    politeness?: 'polite' | 'assertive'

    // ===== QUEUING & DEDUPLICATION (Phase 2+, foundation only) =====

    /**
     * Prevent duplicate messages within time window
     *
     * **Phase 1**: Accepted but not yet active (pass-through)
     * **Phase 2+**: Prevents showing duplicate messages within `dedupeWindow`
     *
     * Default: `false`
     *
     * @example
     * ```typescript
     * // Phase 2+: Prevent "Connection lost" spam during reconnect attempts
     * announce('Connection lost, retrying...', 'warning', {
     *   dedupe: true,
     *   dedupeWindow: 10000  // Max one message every 10 seconds
     * })
     * ```
     */
    dedupe?: boolean

    /**
     * Deduplication time window in milliseconds
     *
     * **Phase 1**: Accepted but not yet active
     * **Phase 2+**: Time window for duplicate message suppression
     *
     * Default: `5000` (5 seconds)
     */
    dedupeWindow?: number

    /**
     * Custom key for deduplication
     *
     * **Phase 1**: Accepted but not yet active
     * **Phase 2+**: Use custom key instead of message content for deduplication
     *
     * Useful when message content varies but represents the same event.
     *
     * @example
     * ```typescript
     * // Phase 2+: Dedupe by operation type, not message content
     * announce(`Saved ${count} items`, 'success', {
     *   dedupe: true,
     *   dedupeKey: 'save-operation'  // Same key even with different counts
     * })
     * ```
     */
    dedupeKey?: string

    /**
     * Add to queue instead of immediate presentation
     *
     * **Phase 1**: Accepted but not yet active (messages shown immediately)
     * **Phase 2+**: Messages are queued and batch-processed
     *
     * Default: `false`
     *
     * @example
     * ```typescript
     * // Phase 2+: Queue multiple validation errors
     * errors.forEach(err => {
     *   announce(err.message, 'error', {
     *     queue: true,
     *     queueStrategy: 'merge'  // Combine into single message
     *   })
     * })
     * ```
     */
    queue?: boolean

    /**
     * Queue processing strategy
     *
     * **Phase 1**: Accepted but not yet active
     * **Phase 2+**: Controls how queued messages are processed
     *
     * - `fifo`: First In First Out (show oldest first)
     * - `lifo`: Last In First Out (show newest first)
     * - `merge`: Combine similar messages into one with details
     *
     * Default: `'fifo'`
     */
    queueStrategy?: 'fifo' | 'lifo' | 'merge'

    /**
     * Maximum queued messages
     *
     * **Phase 1**: Accepted but not yet active
     * **Phase 2+**: Limit queue size to prevent memory issues
     *
     * Default: `100`
     */
    maxQueueSize?: number
}

/**
 * Message types for announce system
 *
 * Defines severity levels for user-facing messages, affecting visual styling,
 * screen reader priority, and default durations.
 *
 * - `error`: Problems requiring user attention (red, 4s default duration)
 * - `success`: Successful operations (green, 3s default duration)
 * - `warning`: Potential issues or cautionary information (yellow, 4s default)
 * - `info`: General informational messages (blue, 3s default duration)
 * - `debug`: Development/debugging messages (gray, 3s default, dev-only)
 */
export type MessageType = 'error' | 'success' | 'warning' | 'info' | 'debug'

/**
 * ARIA politeness levels for screen reader announcements
 *
 * Controls how urgently screen readers announce messages to users.
 *
 * - `polite`: Wait for user to finish current task before announcing (default)
 * - `assertive`: Interrupt user immediately (use sparingly)
 *
 * @see https://www.w3.org/TR/wai-aria-1.1/#aria-live ARIA live regions specification
 */
export type AriaPoliteness = 'polite' | 'assertive'

//=============================================================================
// Unified Announce System (Phase 1, Step 4)
//=============================================================================

/**
 * Unified accessible notification system
 * Displays messages via toast, screen reader, and/or console
 *
 * This is the **PRIMARY API** for all user-facing messages. It provides independent
 * control over three presentation channels:
 * 1. **Toast**: Visual notification (colored boxes with icons)
 * 2. **Screen Reader**: ARIA live region announcements (WCAG 2.1 AA compliant)
 * 3. **Console**: Scope-based grouped logging (development only)
 *
 * @param message - User-friendly message to display
 * @param type - Message type affecting styling and behavior
 * @param options - Channel control and presentation options
 *
 * @example
 * ```typescript
 * // All channels (default - toast + screen reader + console in dev)
 * announce('Domain created successfully', 'success')
 *
 * // Screen reader only (background tasks)
 * announce('Background sync complete', 'info', {
 *   toast: false,
 *   screenReader: true
 * })
 *
 * // With scope-based console grouping
 * announce('Domain validation failed', 'error', {
 *   scope: 'DOMAINS',
 *   console: true
 * })
 *
 * // Custom duration
 * announce('Critical error occurred', 'error', { duration: 8000 })
 * ```
 *
 * @see docs/Message-Handling-SDS.md Section 6.2 (Announce System Specification)
 */
export function announce(
    message: string,
    type: MessageType = 'info',
    options: Partial<AnnounceOptions> = {}
): void {
    // Merge defaults
    const opts: AnnounceOptions = {
        type, // Keep for backward compatibility
        toast: options.toast !== false, // Default: enabled
        screenReader: options.screenReader !== false, // Default: enabled
        console: import.meta.env.DEV && options.console !== false, // Default: dev only
        politeness:
            options.politeness || (type === 'error' || type === 'warning' ? 'assertive' : 'polite'),
        duration: options.duration,
        scope: options.scope,
        tags: options.tags,
        extra: options.extra,

        // Phase 2+ options (accepted but no-op in Phase 1)
        dedupe: options.dedupe || false,
        dedupeWindow: options.dedupeWindow || 5000,
        dedupeKey: options.dedupeKey,
        queue: options.queue || false,
        queueStrategy: options.queueStrategy || 'fifo',
        maxQueueSize: options.maxQueueSize || 100
    }

    // Channel 1: Toast notification (visual)
    if (opts.toast) {
        showToast(message, type, opts.duration)
    }

    // Channel 2: Screen reader announcement (accessibility)
    if (opts.screenReader) {
        announceToScreenReaderChannel(message, opts.politeness)
    }

    // Channel 3: Console logging (debugging)
    if (opts.console) {
        logToConsole(message, type, opts)
    }
}

/**
 * Display toast notification
 * Internal helper for announce system
 */
function showToast(message: string, type: MessageType, duration?: number): void {
    const defaultDurations: Record<MessageType, number> = {
        error: 4000,
        warning: 4000,
        success: 3000,
        info: 3000,
        debug: 3000
    }

    const timeout = duration || defaultDurations[type]

    switch (type) {
        case 'success':
            toast.success(message, { timeout })
            break
        case 'error':
            toast.error(message, { timeout })
            break
        case 'warning':
            toast.warning(message, { timeout })
            break
        case 'debug':
        case 'info':
        default:
            toast.info(message, { timeout })
    }
}

/**
 * Announce to screen readers via ARIA live region
 * Internal helper for announce system
 */
function announceToScreenReaderChannel(
    message: string,
    politeness: AriaPoliteness = 'polite'
): void {
    const announcer = document.getElementById('sr-announcer')
    if (!announcer) {
        console.warn('[announce] Screen reader element #sr-announcer not found')
        return
    }

    // Set politeness level
    announcer.setAttribute('aria-live', politeness)

    // Clear content first to ensure announcement is triggered for repeat messages
    announcer.textContent = ''

    // Small timeout helps assistive technology pick up content changes
    setTimeout(() => {
        announcer.textContent = message
    }, 10)

    // Clear after delay so next message is fresh
    setTimeout(() => {
        announcer.textContent = ''
    }, 1000)
}

/**
 * Log to console with scope-based grouping and metadata
 * Internal helper for announce system
 *
 * Provides enhanced debugging with:
 * - Emoji indicators for quick visual scanning
 * - Scope-based filtering (filter by [DOMAINS], [MAIL], etc.)
 * - Grouped error/warning logs with full context
 * - Compact info/success/debug logs
 */
function logToConsole(message: string, type: MessageType, options: AnnounceOptions): void {
    const timestamp = new Date().toLocaleTimeString()
    const scope = options.scope || 'APP'

    // Type indicators with emoji for quick visual scanning
    const typeIndicators: Record<MessageType, string> = {
        error: '❌',
        warning: '⚠️',
        success: '✅',
        info: 'ℹ️',
        debug: '🔍'
    }
    const indicator = typeIndicators[type] || 'ℹ️'

    // Scope-based group label
    const groupLabel = `${indicator} [${scope}] ${message}`

    // Use grouped logging for errors/warnings (with stack traces and metadata)
    if (type === 'error' || type === 'warning') {
        console.group(groupLabel)

        // Log with appropriate method
        const logMethod = type === 'error' ? console.error : console.warn
        logMethod(message)

        // Add metadata if present
        console.log(`⏰ Timestamp: ${timestamp}`)
        console.log(`📊 Type: ${type}`)
        console.log(`🏷️  Scope: ${scope}`)

        if (options.tags && Object.keys(options.tags).length > 0) {
            console.log('🏷️  Tags:', options.tags)
        }
        if (options.extra && Object.keys(options.extra).length > 0) {
            console.log('📦 Extra:', options.extra)
        }

        console.groupEnd()
    } else {
        // Compact logging for info/success/debug
        const logMethod = type === 'debug' ? console.debug : console.info
        logMethod(groupLabel, `(${timestamp})`)
    }
}

/**
 * Convenience wrapper for success messages
 *
 * @param message - Success message to announce
 * @param duration - Optional duration override
 */
export function announceSuccess(message: string, duration?: number): void {
    announce(message, 'success', { duration })
}

/**
 * Convenience wrapper for error messages
 *
 * @param message - Error message to announce
 * @param duration - Optional duration override (default: 4000ms for errors)
 */
export function announceError(message: string, duration = 4000): void {
    announce(message, 'error', { duration })
}

/**
 * Convenience wrapper for info messages
 *
 * @param message - Info message to announce
 * @param duration - Optional duration override
 */
export function announceInfo(message: string, duration?: number): void {
    announce(message, 'info', { duration })
}

/**
 * Convenience wrapper for warning messages
 *
 * @param message - Warning message to announce
 * @param duration - Optional duration override
 */
export function announceWarning(message: string, duration?: number): void {
    announce(message, 'warning', { duration })
}

/**
 * Convenience wrapper for screen-reader-only messages
 *
 * @param message - Message to announce to screen readers only
 */
export function announceToScreenReaderOnly(message: string): void {
    announce(message, 'info', { toast: false, screenReader: true, console: false })
}

// ❌ announceApiError() has been REMOVED (Step 4)
// Migration: Use the two-step pattern instead:
//
// OLD (removed):
//   import { announceApiError } from '@/utils/announce'
//   announceApiError(error, 'Failed to save')
//
// NEW (use this):
//   import { announce } from '@/utils/announce'
//   import { processError } from '@/composables/utils'
//
//   const message = processError(error, 'Failed to save')
//   announce(message, 'error')
